import {
  ComparisonRow,
  ComparisonValue,
  PositionComparison,
} from '../../models/position-comparison.model'

/** One field row spanning every source that carries it. */
export interface FieldRow {
  label: string
  /** Value per source key, only for sources that actually carry this field. */
  values: Record<string, ComparisonValue>
  /** Source keys that carry this field, in the comparison's source order. */
  presentSources: string[]
}

export interface BucketedFields {
  /** Fields present in two or more sources. */
  compared: FieldRow[]
  /** Fields present in exactly one source, keyed by that source. */
  onlyBySource: Record<string, FieldRow[]>
}

/**
 * Case/space/underscore-insensitive key so "Instrument ID" (ENFUSION) and "Instrument Id"
 * (CUSTODY) group as the same field — matching how the backend's own comparisons map already
 * name-aligns fields across sources (per the design doc: "field alignment is name-driven,
 * identical names, case/space/underscore-insensitive").
 */
const normalizeLabel = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '')

/**
 * Buckets every field label across `comparison.sources` into COMPARED (present in >=2 sources) vs
 * `<SOURCE> ONLY` (present in exactly one), preserving first-seen field order within each bucket and
 * source order for `onlyBySource`'s keys. Grouped by normalized label (see {@link normalizeLabel});
 * the row displays the first-seen literal spelling, which is the anchor's since sources are anchor-
 * first.
 */
export function bucketFields(comparison: PositionComparison): BucketedFields {
  const rowsByLabel = new Map<string, FieldRow>()

  for (const source of comparison.sources) {
    for (const field of source.fields) {
      const key = normalizeLabel(field.label)
      let row = rowsByLabel.get(key)
      if (!row) {
        row = { label: field.label, values: {}, presentSources: [] }
        rowsByLabel.set(key, row)
      }
      row.values[source.source] = field.value
      // A source can list the same label twice (e.g. under two different column groups) — that's
      // still one source, not two, so only count it once toward "present in >=2 sources".
      if (!row.presentSources.includes(source.source)) {
        row.presentSources.push(source.source)
      }
    }
  }

  const compared: FieldRow[] = []
  const onlyBySource: Record<string, FieldRow[]> = {}
  for (const source of comparison.sources) onlyBySource[source.source] = []

  for (const row of rowsByLabel.values()) {
    if (row.presentSources.length >= 2) {
      compared.push(row)
    } else {
      onlyBySource[row.presentSources[0]].push(row)
    }
  }

  return { compared, onlyBySource }
}

/** The non-anchor source keys with a comparison section — the Difference picker's choices. */
export function differenceKeys(comparison: PositionComparison): string[] {
  return Object.keys(comparison.comparisons)
}

function findComparisonRow(
  comparison: PositionComparison,
  differenceKey: string,
  label: string,
): ComparisonRow | null {
  const rows = comparison.comparisons[differenceKey]
  const target = normalizeLabel(label)
  return rows?.find(row => normalizeLabel(row.label) === target) ?? null
}

/** What the Difference column shows for one COMPARED row + the color it's shown in. */
export interface DiffDisplay {
  text: string
  tone: 'match' | 'break' | 'missing'
}

const MISSING_DIFF: DiffDisplay = { text: '—', tone: 'missing' }

/**
 * The Difference-column content for one COMPARED field against the anchor, for the given
 * Difference-picker key. Rendered verbatim from the server's own `verdict` — nothing is computed
 * client-side:
 *  - No diff row for this (key, label) pair at all → grey "—" (MISSING).
 *  - `verdict` is a number (the backend's own precomputed delta, for numeric fields) → that number
 *    as-is; green when exactly 0, red otherwise.
 *  - `verdict` is 'MATCH' / 'BREAK' (string fields) → a green ✓ / a red ≠.
 */
export function diffDisplayFor(
  comparison: PositionComparison,
  differenceKey: string,
  label: string,
): DiffDisplay {
  const row = findComparisonRow(comparison, differenceKey, label)
  if (!row) return MISSING_DIFF

  if (typeof row.verdict === 'number') {
    return {
      text: String(row.verdict),
      tone: row.verdict === 0 ? 'match' : 'break',
    }
  }

  return row.verdict === 'MATCH'
    ? { text: '✓', tone: 'match' }
    : { text: '≠', tone: 'break' }
}

/** Case-insensitive substring match on a field row's label, for the Search Table box. */
export function matchesSearch(row: FieldRow, search: string): boolean {
  if (!search.trim()) return true
  return row.label.toLowerCase().includes(search.trim().toLowerCase())
}

/** The row's display value for one source column, or null when absent/null/empty (renders as —).
 *  The backend sends the literal string "null" for some absent fields (the same quirk seen on
 *  the read API's Enfusion envelope — see row.adapter.ts), not JSON null, so that has to be
 *  checked for explicitly. */
export function resolveCellValue(
  row: FieldRow,
  source: string,
): ComparisonValue {
  const value = row.values[source]
  return value === undefined ||
    value === null ||
    value === '' ||
    value === 'null'
    ? null
    : value
}
